import React, { useRef, useState } from "react";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { MutationResult, MutateFunction } from "react-query";

import {
  ApiErrorBanner,
  Button,
  Dialog,
  Loader,
  TextInput,
  Checkbox,
} from "fe-shared/src/components";

import { DialogHeader } from "fe-shared-transactions/src/components";
import {
  TransactionSection,
  TransactionType,
} from "fe-shared-transactions/src/types";
import { ApiError } from "fe-shared/src/types/common";
import { FLAG_LENGTH_LIMIT } from "consts";
import { ActionFlagParams } from "routes/TransactionDetails/modules/api";
import styles from "./FlagDialog.module.scss";

interface Props {
  transactionId: number;
  sectionCode: TransactionSection;
  isActive: boolean;
  onClose: () => void;
  onSubmit: MutateFunction<
    void,
    AxiosError<ApiError>,
    ActionFlagParams,
    unknown
  >;
  mutationResult: MutationResult<void, AxiosError<ApiError>>;
  text?: string;
  showToParticipant?: boolean;
  transactionNumber: string;
  isEditDialog?: boolean;
  transactionType: TransactionType;
}

const FlagDialog = (props: Props) => {
  const {
    transactionId,
    sectionCode,
    isActive,
    onClose,
    onSubmit,
    mutationResult,
    transactionNumber,
    transactionType,
    text = "",
    showToParticipant = false,
    isEditDialog = false,
  } = props;
  const { isLoading, isError } = mutationResult;
  const { t } = useTranslation();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [flagCommentDraft, setFlagCommentDraft] = useState(text);
  const [isVisibleToParticipant, setVisibleParticipant] = useState(
    showToParticipant
  );

  const isCommentTooLong = flagCommentDraft.length > FLAG_LENGTH_LIMIT;
  const characterLimitText = `${flagCommentDraft.length.toLocaleString()}/${FLAG_LENGTH_LIMIT.toLocaleString()}`;
  const isAdjustment = transactionType === TransactionType.Adjustment;

  const title = (
    <DialogHeader
      type={transactionType}
      number={transactionNumber}
      title={t("transactions.actions.whatIsWrong")}
      translationRoot="transactions"
    />
  );
  const actions = [
    <Button
      key="1"
      disabled={!flagCommentDraft || isCommentTooLong}
      id="flag-comment-dialog-save-btn"
      onClick={() => {
        onSubmit({
          transactionId,
          sectionCode,
          text: flagCommentDraft,
          isVisibleToParticipant,
        });
      }}
    >
      {t(`transactions.actions.${isEditDialog ? "editFlag" : "addFlag"}`)}
    </Button>,
    <Button
      key="2"
      onClick={onClose}
      variant="outlined"
      id="flag-comment-dialog-cancel-btn"
    >
      {t("transactions.actions.cancel")}
    </Button>,
  ];

  return (
    <Dialog
      width={560}
      title={title}
      onClose={onClose}
      actions={actions}
      isActive={isActive}
    >
      {isLoading && <Loader isBlocking />}
      {isError && (
        <ApiErrorBanner
          requestStatus={mutationResult}
          id="flag-comment-dialog-error-banner"
        />
      )}
      <TextInput
        isMultiline
        ref={inputRef}
        value={flagCommentDraft}
        isError={isCommentTooLong}
        id="flag-comment-dialog-input"
        onChange={(e, value) => setFlagCommentDraft(value)}
        errorText={
          isCommentTooLong
            ? `${t(
                "transactions.actions.characterLimit"
              )} ${characterLimitText}`
            : ""
        }
      />
      {!isAdjustment && (
        <div className={styles.checkbox}>
          <Checkbox
            id="is-show-participant-checkbox"
            title={t("transactions.actions.showToParticipant")}
            onChange={(e, v) => setVisibleParticipant(v)}
            value={isVisibleToParticipant}
          />
        </div>
      )}
    </Dialog>
  );
};

export default FlagDialog;
